import React, { useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Checkbox, TextArea, TextInput, Select } from '../../../Form';
import Label from '../Label';
import clsx from 'clsx';
import useCheckoutState from '../../../../hooks/useCheckoutState';
import isFieldHidden from './isFieldHidden';
import isFieldEnabled from './isFieldEnabled';
import CountryField from '../CountryField';
import TaxIdField from '../TaxIdField';
import QuantityButton from '../QuantityButton';
import QuantityField from '../QuantityField';
import EmailField from '../EmailField';
import PhoneField from '../PhoneField';
import NumberInput from '../../../Form/components/NumberInput';

const FormFieldItem = ({ className, field, horizontal, children }) => {
  const { isPreview, page, form } = useCheckoutState();

  const enabled = useMemo(
    () => isFieldEnabled(field, form.fields, page.fields),
    [field, form.fields, page.fields],
  );

  // do not use field as a dependency here, as it will cause a hidden+required field to hide when it is being typed into, as the field value will change
  const hidden = useMemo(() => isFieldHidden(field, isPreview), [isPreview]);

  const shouldHideField = hidden || !enabled;

  if (shouldHideField && !isPreview) return null;

  return (
    <div
      className={clsx('cp_form-item mb-4', className, {
        hidden: shouldHideField && !isPreview,
        'opacity-25': shouldHideField && isPreview,
        'flex items-center': horizontal,
      })}
    >
      {children}
    </div>
  );
};

const FormField = ({ field, onChange, onBlur }) => {
  const label = field.label?.trim();

  switch (field.element) {
    case 'checkbox':
      return (
        <FormFieldItem
          field={field}
          horizontal
          style={{ display: 'flex', alignItems: 'center' }}
          className={`cp_form-item cp_form-item-${field.element}`}
        >
          <Checkbox
            id={field._id}
            name={field._id}
            type={field.element}
            value={field.value}
            onChange={e => onChange(e.target.value)}
            required={field.required}
            style={{ marginRight: '0.5em', marginBottom: '0' }}
            className="cp_form-checkbox"
            onBlur={onBlur}
          />
          {label && (
            <Label
              htmlFor={field._id}
              style={{ marginBottom: '0px' }}
              className="cp_form-label cp_form-checkbox-label"
              required={field.required}
            >
              {label}
            </Label>
          )}
        </FormFieldItem>
      );
    case 'textarea':
      return (
        <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
          {label && (
            <Label htmlFor={field._id} className="cp_form-label" required={field.required}>
              {label}
            </Label>
          )}
          <TextArea
            id={field._id}
            name={field._id}
            type={field.element}
            placeholder={field.placeholder || label}
            value={field.value}
            onChange={e => onChange(e.target.value)}
            required={field.required}
            className="cp_form-textarea"
            onBlur={onBlur}
          />
        </FormFieldItem>
      );
    case 'number':
      return (
        <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
          {label && (
            <Label htmlFor={field._id} className="cp_form-label" required={field.required}>
              {label}
            </Label>
          )}

          <NumberInput
            id={field._id}
            name={field._id}
            placeholder={field.placeholder || label}
            type="number"
            value={field.value}
            onChange={e => onChange(e.target.value)}
            required={field.required}
            className="cp_form-textinput"
            onBlur={onBlur}
          />
        </FormFieldItem>
      );
    case 'select':
      return (
        <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
          {label && (
            <Label htmlFor={field._id} className="cp_form-label" required={field.required}>
              {label}
            </Label>
          )}
          <Select
            onChange={e => {
              const index = e.nativeEvent.target.selectedIndex;
              const selectedOptionLabel = e.nativeEvent.target[index].text;

              onChange(e.target.value, { selectedOptionLabel });
            }}
            key={field._id}
            required={field.required}
            value={field.value}
            defaultValue=""
            className="cp_form-select"
            onBlur={onBlur}
          >
            <option value="" disabled={field.required} className="cp_form-option">
              {field.placeholder || 'Select an option'}
            </option>

            {field.options?.map(option => (
              <option
                value={option.value}
                key={option._id}
                className={`cp_form-option flex justify-between`}
              >
                {option.label}
              </option>
            ))}
          </Select>
        </FormFieldItem>
      );
    case 'multiple-choice':
      return (
        <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
          {label && (
            <Label htmlFor={field._id} className="cp_form-label" required={field.required}>
              {label}
            </Label>
          )}

          <fieldset>
            <legend className="sr-only">{label}</legend>

            <div className="space-y-1">
              {field.options?.map(option => (
                <div key={option._id} className="flex items-center">
                  <input
                    onBlur={onBlur}
                    id={option._id}
                    name={field._id}
                    type="radio"
                    onChange={() => onChange(option.label)}
                    value={option.label}
                    checked={field.value === option.label}
                    required={field.required}
                    className="cp_form-radio h-[18px] w-[18px] border-none shadow-input focus:shadow-input-focus focus:ring-[var(--color-primary)] text-[var(--color-primary)]"
                  />

                  <label htmlFor={option._id} className="cp_form-radio-label ml-2">
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </fieldset>
        </FormFieldItem>
      );
    case 'quantity':
      return (
        <FormFieldItem
          field={field}
          horizontal
          className={`cp_form-item cp_form-item-${field.element}`}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {label && (
            <Label htmlFor={field._id} className="cp_form-label" required={field.required}>
              {label}
            </Label>
          )}
          <div className="flex items-stretch ml-2">
            <QuantityButton
              className="cp_quantity-button-minus"
              onClick={() => {
                if (field.value <= 1) {
                  return;
                }

                onChange(Number(field.value - 1));
                onBlur();
              }}
            >
              <MinusIcon className="cp_quantity-button-icon cp_quantity-button-minus-icon w-5 h-5" />
            </QuantityButton>

            <QuantityField
              id={field._id}
              name={field._id}
              value={field.value}
              onChange={onChange}
              required={true}
              min={field.minValue?.enabled ? field.minValue.value : 1}
              max={field.maxValue?.enabled ? field.maxValue.value : 9999}
            />

            <QuantityButton
              className="cp_quantity-button-plus"
              type="button"
              onClick={() => {
                onChange(Number(field.value + 1));
                onBlur();
              }}
            >
              <PlusIcon className="cp_quantity-button-icon cp_quantity-button-plus-icon w-5 h-5" />
            </QuantityButton>
          </div>
        </FormFieldItem>
      );
    case 'country':
      return (
        <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
          <CountryField field={field} onChange={onChange} onBlur={onBlur} />
        </FormFieldItem>
      );
    case 'tax-id':
      return (
        <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
          <TaxIdField field={field} onChange={onChange} onBlur={onBlur} />
        </FormFieldItem>
      );
    case 'email':
      return (
        <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
          <EmailField field={field} onChange={onChange} onBlur={onBlur} />
        </FormFieldItem>
      );
    case 'date-time': {
      return (
        <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
          {label && (
            <Label htmlFor={field._id} className="cp_form-label" required={field.required}>
              {label}
            </Label>
          )}

          <TextInput
            id={field._id}
            name={field._id}
            type="datetime-local"
            value={field.value}
            onChange={e => {
              try {
                const date = new Date(e.target.value);
                if (isNaN(date.getTime())) {
                  return;
                }

                onChange(e.target.value);
              } catch (error) {
                return;
              }
            }}
            required={field.required}
            className="cp_form-textinput"
            min={field.minValue?.enabled ? field.minValue.value : ''}
            max={field.maxValue?.enabled ? field.maxValue.value : ''}
            onBlur={onBlur}
          />
        </FormFieldItem>
      );
    }
    case 'phone':
      return (
        <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
          <PhoneField field={field} onChange={onChange} onBlur={onBlur} />
        </FormFieldItem>
      );
    case 'po-number':
      return (
        <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
          {label?.length > 0 && (
            <Label htmlFor={field._id} className="cp_form-label" required={field.required}>
              {label}
            </Label>
          )}

          <TextInput
            id={field._id}
            name={field._id}
            type="text"
            placeholder={field.placeholder || label}
            value={field.value}
            onChange={e => onChange(e.target.value)}
            required={field.required}
            className="cp_form-textinput"
            min={field.minValue?.enabled ? field.minValue.value : ''}
            max={field.maxValue?.enabled ? field.maxValue.value : ''}
            onBlur={onBlur}
          />
        </FormFieldItem>
      );
    case 'text':
    default:
      return (
        <FormFieldItem field={field} className={`cp_form-item cp_form-item-${field.element}`}>
          {label?.length > 0 && (
            <Label htmlFor={field._id} className="cp_form-label" required={field.required}>
              {label}
            </Label>
          )}

          <TextInput
            id={field._id}
            name={field._id}
            type={field.element}
            placeholder={field.placeholder || label}
            value={field.value}
            onChange={e => onChange(e.target.value)}
            required={field.required}
            className="cp_form-textinput"
            min={field.minValue?.enabled ? field.minValue.value : ''}
            max={field.maxValue?.enabled ? field.maxValue.value : ''}
            onBlur={onBlur}
          />
        </FormFieldItem>
      );
  }
};

FormField.propTypes = {
  onChange: PropTypes.func,
  field: PropTypes.shape({
    _id: PropTypes.string,
    element: PropTypes.oneOf(['text', 'textarea', 'email', 'select', 'checkbox']),
    placeholder: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.string,
    required: PropTypes.bool,
  }),
};

export default FormField;
